import checkIfResponseSuccess from 'owa-mail-store/lib/services/utils/checkIfResponseSuccess';
import type ItemInfoResponseMessage from 'owa-service/lib/contract/ItemInfoResponseMessage';
import type { RequestQueue } from 'owa-service-utils';
import { createRequestQueue } from 'owa-service-utils';
import { getItems } from 'owa-mail-store/lib/services/getItems';
import { getTimestamp, now } from 'owa-datetime';
import { itemResponseShapeWithAdditionalProperties } from 'owa-mail-store/lib/services/utils/iitemResponseShapeWithAdditionalProperties';
import { parseGetItemsResponse } from '../actions/parseGetItemsResponse';
import type { RowInfoToGetRSVP } from 'mini-common-schema/lib/schema/RowInfoToGetRSVP';
import { trace } from 'owa-trace';

const MAX_RETRY_COUNT = 2;
const MAXIMUM_NUM_REQUESTS = 3;
let requestQueue: RequestQueue = null;

/**
 * Return an instance of RequestQueue
 * Allows for MAXIMUM_NUM_REQUESTS in parallel and queues the rest for execution
 */
const getRequestQueue = () => {
    if (!requestQueue) {
        requestQueue = createRequestQueue(MAXIMUM_NUM_REQUESTS);
    }
    return requestQueue;
};

// Exporting for testing only
export const loadItemWithErrorHandling = async (
    infoForRsvpCandidateRows: RowInfoToGetRSVP[],
    additionalPropertiesInfo: string[],
    retryCount?: number,
    onSuccessfulCompletion?: (timestampOfCompletion: number) => void
) => {
    if (retryCount > MAX_RETRY_COUNT) {
        return;
    }

    const itemIds = infoForRsvpCandidateRows.map(rowInfo => rowInfo.itemId);

    const { add: addToQueue } = getRequestQueue();

    try {
        const responseMessages = await addToQueue(() =>
            getItems(
                itemIds,
                itemResponseShapeWithAdditionalProperties(
                    {
                        BaseShape: 'IdOnly',
                    },
                    ...additionalPropertiesInfo
                ),
                null // shapeName
            )
        );

        if (!responseMessages) {
            trace.warn('[loadingmeetingmessage] no Items received in GI call response');
        } else {
            const timestampOfCompletion = getTimestamp(now());
            const succeededResponseItems = responseMessages
                .filter(checkIfResponseSuccess)
                .map(responseMessage => (responseMessage as ItemInfoResponseMessage).Items?.[0]);

            parseGetItemsResponse(succeededResponseItems);

            onSuccessfulCompletion?.(timestampOfCompletion);

            // If at least one of the responses failed, lets add some traces on these Ids
            if (succeededResponseItems.length !== responseMessages.length) {
                trace.warn(`[loadingmeetingmessage] some items failed in: ${itemIds}`);
            }
        }
    } catch (error) {
        trace.warn(`[loadingmeetingmessage] all items failed in: ${itemIds}`);
        loadItemWithErrorHandling(
            infoForRsvpCandidateRows,
            additionalPropertiesInfo,
            retryCount + 1,
            onSuccessfulCompletion
        );
    }
};
